import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import Select, { components } from 'react-select';

const SortableMultiValue = (props) => {
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    id: props.data.value,
  });

  const onMouseDown = (event) => {
    // Keep beginning a chip drag from opening/closing the react-select menu.
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        display: 'inline-flex',
        maxWidth: '100%',
        position: isDragging ? 'relative' : undefined,
        touchAction: 'none',
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 40 : undefined,
      }}
      {...attributes}
      {...listeners}
    >
      <components.MultiValue {...props} innerProps={{ ...props.innerProps, onMouseDown }} />
    </div>
  );
};

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <ChevronDownIcon className="ml-2 h-5 w-5 text-[#9A9285]" />
  </components.DropdownIndicator>
);

const IndicatorSeparator = () => null;

export default function MultiSelectDropdown(props) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      border: state.selectProps.error
        ? '1px solid rgb(239, 68, 68)' // red-500
        : '1px solid rgb(224, 217, 204)', // edge
      backgroundColor: '#FFFFFF',
      boxShadow: 'none',
      cursor: 'default',
      borderRadius: '0.375rem',
      maxHeight: 'fit-content',
      minHeight: props.height ? `${props.height}px` : 'auto',
      overflow: 'visible',
    }),
    container: (provided) => ({
      ...provided,
      position: 'relative',
      maxHeight: 'fit-content',
      minHeight: props.height ? `${props.height}px` : 'auto',
    }),
    multiValue: (provided) => ({
      ...provided,
      height: 'auto',
      borderRadius: '0.25rem',
      maxHeight: props.height ? `${props.height - 12}px` : 'auto',
      alignItems: 'center',
      display: 'flex',
      margin: '0px 0px',
    }),
    valueContainer: (provided) => ({
      ...provided,
      paddingLeft: '6px',
      gap: '4px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'rgb(107, 100, 88)',
      fontSize: '14px',
    }),
    input: (provided) => ({
      ...provided,
      height: props.height ? `${props.height - 8}px` : 'auto',
      minHeight: props.height ? `${props.height - 8}px` : 'auto',
    }),
    loadingIndicator: (provided) => ({
      ...provided,
      height: props.height ? `${props.height - 8}px` : 'auto',
      minHeight: props.height ? `${props.height - 8}px` : 'auto',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 1000,
    }),
    menu: (provided) => ({
      ...provided,
      marginLeft: '1px',
      marginRight: '-1px',
      width: 'calc(100% - 2px)',
      backgroundColor: '#FCFBF8',
      border: '1px solid #E8E4DC',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#F0ECE4' : state.isFocused ? '#F5F3ED' : 'transparent',
      color: '#3A3429',
      cursor: 'pointer',
      ':active': {
        backgroundColor: '#F0ECE4',
      },
    }),
  };

  const setItems = (items) => {
    if (props.dataIndex !== undefined) {
      props.setItems(items, props.dataIndex);
    } else {
      props.setItems(items);
    }
  };

  const handleChange = (newValue) => {
    setItems(newValue.map((item) => item.value));
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = props.items.indexOf(active.id);
    const newIndex = props.items.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    setItems(arrayMove(props.items, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={props.items} strategy={rectSortingStrategy}>
        <Select
          inputId={props.inputId}
          aria-label={props.ariaLabel}
          height={props.height ? props.height : 'auto'}
          error={props.error}
          isMulti
          onChange={handleChange}
          options={props.options}
          styles={customStyles}
          value={props.items.map((item) => ({ value: item, label: item }))}
          components={{
            MultiValue: SortableMultiValue,
            DropdownIndicator,
            IndicatorSeparator,
          }}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          menuPosition="fixed"
          closeMenuOnSelect={false}
          isClearable={false}
        />
      </SortableContext>
    </DndContext>
  );
}
